import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Star, MessageSquare, Quote, User, Building } from "lucide-react";
import { useTranslation } from 'react-i18next';
import { ReviewModal } from "@/components/review-modal";
import { supabase } from "@/integrations/supabase/client";

interface Review {
  id: string;
  name: string;
  company: string;
  message: string;
  rating: number;
  created_at: string;
}

export function TestimonialsSection() {
  const { t } = useTranslation();
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadReviews();
  }, []);

  const loadReviews = async () => {
    try {
      const { data, error } = await supabase
        .from('reviews')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading reviews:', error);
      } else {
        setReviews(data || []);
      }
    } catch (error) {
      console.error('Error loading reviews:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenReviewModal = () => {
    setIsReviewModalOpen(true);
  };

  const handleReviewAdded = () => {
    loadReviews(); // Reload reviews when a new one is added
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${
          i < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
        }`}
      />
    ));
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCompanyAvatar = (company: string) => {
    const companyLower = company.toLowerCase();
    if (companyLower.includes('solar') || companyLower.includes('placas') || companyLower.includes('energía') || companyLower.includes('energia')) {
      return '☀️';
    }
    if (companyLower.includes('reforma') || companyLower.includes('construcción') || companyLower.includes('construccion') || companyLower.includes('obra')) {
      return '🏗️';
    }
    return '🏢'; // Default for general companies
  };

  return (
    <section className="py-12 sm:py-20 bg-muted/20">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="text-center mb-12">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">
            ¿Trabajaste con <span className="text-primary">nosotros?</span>
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
            Nos encantaría conocer tu experiencia. Tu opinión nos ayuda a mejorar y ayuda a otros empresarios a tomar la mejor decisión.
          </p>
          
          <div className="bg-primary/5 rounded-xl p-12 border border-primary/10 max-w-lg mx-auto">
            <div className="flex justify-center mb-6">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className="h-8 w-8 fill-yellow-400 text-yellow-400"
                />
              ))}
            </div>
            
            <h3 className="text-xl font-bold mb-4 text-foreground">
              Comparte tu experiencia
            </h3>
            
            <p className="text-muted-foreground mb-6">
              Ayuda a otros empresarios como tú compartiendo cómo MKTN Guardians ha transformado tu negocio.
            </p>
            
            <Button 
              onClick={handleOpenReviewModal}
              size="lg"
              className="w-full"
            >
              <MessageSquare className="h-5 w-5 mr-2" />
              Dejar mi reseña
            </Button>
          </div>
        </div>

        {/* Sección de reseñas existentes */}
        {reviews.length > 0 && (
          <div className="mt-16">
            <div className="text-center mb-12">
              <h3 className="text-2xl sm:text-3xl font-bold mb-4">
                Lo que dicen nuestros <span className="text-primary">clientes</span>
              </h3>
              <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
                Experiencias reales de empresarios que han transformado su negocio con MKTN Guardians.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {reviews.map((review, index) => (
                <Card key={review.id} className="h-full bg-card/80 backdrop-blur border-primary/20 hover:shadow-lg transition-shadow duration-300">
                  <CardContent className="p-6 h-full flex flex-col">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="text-3xl">{getCompanyAvatar(review.company)}</div>
                        <div>
                          <h4 className="font-semibold text-foreground">{review.name}</h4>
                          <p className="text-sm font-medium text-primary">{review.company}</p>
                          <p className="text-xs text-muted-foreground">{formatDate(review.created_at)}</p>
                        </div>
                      </div>
                      <Quote className="h-6 w-6 text-primary/30" />
                    </div>

                    {/* Rating */}
                    <div className="flex items-center gap-2 mb-4">
                      <div className="flex">{renderStars(review.rating)}</div>
                      <span className="text-sm text-muted-foreground">({review.rating}/5)</span>
                    </div>

                    {/* Content */}
                    <blockquote className="text-foreground mb-4 flex-1 leading-relaxed">
                      "{review.message}"
                    </blockquote>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {isLoading && (
          <div className="text-center mt-12">
            <p className="text-muted-foreground">Cargando reseñas...</p>
          </div>
        )}

        <ReviewModal 
          isOpen={isReviewModalOpen} 
          onClose={() => {
            setIsReviewModalOpen(false);
            handleReviewAdded();
          }} 
        />
      </div>
    </section>
  );
}